/**
 * Utility functions for mapping between UI category labels and API category values
 */

import { createLogger } from "../../features/notifications/utils/logger";

const logger = createLogger("categoryMapping");

export type UICategory = "Global" | "Account" | "Platform" | "Client";
export type APICategory = "OPTUM" | "PLATFORM" | "CLIENT";

/**
 * Maps UI category labels to API category values
 * @param uiCategory - The UI category label (Global, Account, Platform, Client)
 * @returns The corresponding API category value (OPTUM, PLATFORM, CLIENT)
 */
export const mapUICategoryToAPI = (uiCategory: UICategory | string): APICategory => {
  const mapping: Record<string, APICategory> = {
    Global: "OPTUM",
    Account: "PLATFORM",
    Platform: "PLATFORM",
    Client: "CLIENT",
    // Allow pass-through for API values
    OPTUM: "OPTUM",
    PLATFORM: "PLATFORM",
    CLIENT: "CLIENT",
  };

  const result = mapping[uiCategory];
  if (result === undefined) {
    logger.warn(`mapUICategoryToAPI: unknown category "${uiCategory}", falling back to "CLIENT"`);
    return "CLIENT";
  }
  return result;
};

/**
 * Maps API category values to UI category labels
 * @param apiCategory - The API category value (OPTUM, PLATFORM, CLIENT)
 * @returns The corresponding UI category label (Global, Account, Client)
 */
export const mapAPICategoryToUI = (apiCategory: APICategory | string): UICategory => {
  const mapping: Record<string, UICategory> = {
    OPTUM: "Global",
    PLATFORM: "Account",
    CLIENT: "Client",
    // Allow pass-through for UI values
    Global: "Global",
    Account: "Account",
    Platform: "Account",
    Client: "Client",
  };

  const result = mapping[apiCategory];
  if (result === undefined) {
    logger.warn(`mapAPICategoryToUI: unknown category "${apiCategory}", falling back to "Client"`);
    return "Client";
  }
  return result;
};
